import { MailboxRef } from '../commsShared/mailboxes'
import { SEND_KIND_LABEL, sendKindLabel } from '../commsInboxView/shared'

/**
 * Buckets used by both the mailbox funnel and conversation lists.
 *   waiting / read / archived   where the mailbox's mail is in the pipeline
 *   letter:<kind>               the container for emails answering one type of
 *                               letter we sent (letters of complaint, chasers…)
 */
export type LetterBucket = `letter:${string}`
/** Route address that means "every mailbox" on the workspace. */
export const ALL_MAILBOXES = 'all'

export type Bucket = 'waiting' | 'read' | 'archived' | LetterBucket
export const isLetterBucket = (b: string | null | undefined): b is LetterBucket => typeof b === 'string' && b.startsWith('letter:')
export const letterBucket = (kind: string): LetterBucket => `letter:${kind}`
export const letterKindOf = (b: string) => (isLetterBucket(b) ? b.slice(7) : null)
export const isBucket = (b: string | null | undefined): b is Bucket => b === 'waiting' || b === 'read' || b === 'archived' || isLetterBucket(b)
export type ConversationState = 'awaiting_lender' | 'awaiting_us' | 'awaiting_client' | 'closed' | 'unknown'
export type CounterpartyKind = 'lender' | 'client' | 'other'

/** Who a group of emails came from: a client, a lender, or neither (unknown, internal, court, FOS). */
export interface SenderCounts {
    client: number
    lender: number
    other: number
}

export interface CategoryFunnel {
    key: string | null
    label: string
    description: string | null
    nextAction: string | null
    partyScope: string | null
    total: number
    linkedLetter: number
    linkedClaim: number
    notLinked: number
    differs: number
    last7d: number
    from?: SenderCounts
}

/** Category breakdown inside one letter container. */
export interface LetterCategoryFunnel {
    key: string | null
    label: string
    description: string | null
    nextAction: string | null
    total: number
    differs: number
    last7d: number
}

/** Emails in this mailbox that answer one type of letter we sent. */
export interface LetterFunnel {
    kind: string
    label: string
    bucket: LetterBucket
    total: number
    waiting: number
    last7d: number
    lastReceivedAt: string | null
    byCategory: LetterCategoryFunnel[]
}

export interface ConversationCounts {
    conversations: number
    /** Threads with at least one email received (the Received tab's "All" in the Threads view). */
    withInbound: number
    archived: number
    waiting: number
    read: number
    /** Conversations per letter type (send kind) that contain a reply to that letter. */
    byLetter: Record<string, number>
    awaitingUs: number
    awaitingLender: number
    awaitingClient: number
    agentOwned: number
    last7d: number
}

export interface MailboxFunnel {
    address: string
    held: number
    archived: number
    waiting: number
    waitingFailed: number
    read: number
    linkedLetter: number
    linkedClaim: number
    notLinked: number
    notLinkedNoneFound: number
    notLinkedSeveral: number
    differs: number
    last7d: number
    lastReceivedAt: string | null
    /** Non-archived emails by who sent them. */
    from?: SenderCounts
    byCategory: CategoryFunnel[]
    byLetter: LetterFunnel[]
    mailbox: MailboxRef & { defaultPartyType: string | null }
    conversations: ConversationCounts | null
}

export interface OverviewResponse {
    success: boolean
    scope: string | null
    mailboxes: MailboxFunnel[]
    totals: Omit<MailboxFunnel, 'address' | 'mailbox' | 'conversations'> & { conversations: ConversationCounts | null }
    registeredMailboxes: (MailboxRef & { defaultPartyType: string | null })[]
    buckets: Bucket[]
    letterKinds: { key: string; label: string; bucket: LetterBucket }[]
}

export interface ConversationListItem {
    id: string
    mailbox: string | null
    caseId: string | null
    claimRef: string | null
    lenderName: string | null
    counterpartyKind: CounterpartyKind
    counterpartyAddress: string | null
    subject: string | null
    state: ConversationState
    /** Category of the newest email (for display only; automated mail counts). */
    latestCategory: string | null
    /** Where the thread stands: category of the newest email that moves the outcome. null = none yet. */
    outcomeCategory: string | null
    outcomeAt: string | null
    outcomeInboundId: string | null
    /** Emails in the thread that never move the outcome (bounces, auto replies, sales). */
    automatedCount: number
    ownerKind: 'person' | 'agent'
    summary: string | null
    summaryAt: string | null
    suggestedNextAction: string | null
    messageCount: number
    inboundCount: number
    outboundCount: number
    unreadCount: number
    firstMessageAt: string | null
    lastMessageAt: string | null
    lastInboundAt: string | null
    lastOutboundAt: string | null
    archived: boolean
    latest: {
        inboundId: string
        subject: string | null
        fromEmail: string
        classification: string | null
        aiSummary: string | null
        linkState: string
        extractionState: string
        caseId: string | null
        receivedAt: string | null
    } | null
}

export interface TrailMessageBase {
    id: string
    role: 'letter' | 'reply' | 'our_reply' | 'forward'
    position: number
    at: string | null
    fromEmail: string | null
    toEmail: string | null
    subject: string | null
}

export interface TrailOutbound extends TrailMessageBase {
    kind: 'outbound'
    status: string
    deliveredAt: string | null
    claimRef: string | null
    lenderName: string | null
    sendKind: string | null
    batchId: string | null
    caseId: string | null
}

export interface TrailInbound extends TrailMessageBase {
    kind: 'inbound'
    classification: string | null
    classificationSource: string | null
    classificationConfidence: number | null
    aiSummary: string | null
    suggestedNextAction: string | null
    linkState: string
    unlinkedReason: string | null
    extractionState: string
    preprocessState: string
    triageState: string
    matchedSendId: string | null
    /** Letter linking: type of the letter this email answers, when one is chosen. */
    sendKind?: string | null
    caseId: string | null
    partyType: string | null
    sourceMailbox: string | null
    sourceFolder: string | null
    sourceProvider: string | null
    attachmentCount: number
}

export type TrailMessage = TrailOutbound | TrailInbound

export interface Participant {
    address: string
    displayName: string | null
    role: 'client' | 'lender' | 'us' | 'other'
    firstSeenAt: string | null
    lastSeenAt: string | null
    messageCount: number
}

export interface ConversationEvent {
    id: string
    kind: string
    actorKind: string
    actor: string | null
    payload: Record<string, unknown> | null
    createdAt: string
}

export interface ConversationDetail {
    conversation: ConversationListItem
    messages: TrailMessage[]
    participants: Participant[]
    events: ConversationEvent[]
    identity: {
        introducer: string | null
        lenderName: string | null
        clientName: string | null
        clientPostcode: string | null
        clientEmail: string | null
        agreementNumber: string | null
        vehicleRegistration: string | null
        vehicle: string | null
        proclaimReference: string | null
    } | null
    cases: { id: string; claimRef: string; lenderNormalisedName: string | null; status: string; ackDeadline: string | null; substantiveDeadline: string | null }[]
    classifications: string[]
    /** threadSummaryEnabled runtime setting: when true the trail offers "Summarise now". */
    threadSummaryEnabled?: boolean
    /** Deep dive labels on this thread, one per outcome it has been sub-categorised under, with the quoted evidence. */
    subcategories?: ThreadSubcategoryLabel[]
}

export interface ThreadSubcategoryLabel {
    parentKey: string
    subcategory: { id: string; key: string; label: string; description: string | null; suggestedAction: { type: string | null; label: string; description: string | null; timing: string | null } | null }
    outcomeInboundId: string | null
    details: string[]
    evidence: { detail: string; quote: string; inboundId: string | null; offset: number | null; verified: boolean }[]
    evidenceState: 'verified' | 'needs_check' | 'human'
    flags: string[]
    confidence: number | null
    rationale: string | null
    model: string | null
    labelledBy: string | null
    labelledAt: string
}

const FIXED_BUCKET_LABEL: Record<'waiting' | 'read' | 'archived', string> = {
    waiting: 'Waiting to be read',
    read: 'Read and categorised',
    archived: 'Archived',
}

const FIXED_BUCKET_HINT: Record<'waiting' | 'read' | 'archived', string> = {
    waiting: 'Pulled from the mailbox but not yet read by the pipeline, so no category yet.',
    read: 'Read and categorised. Everything in the mailbox that is not waiting or archived.',
    archived: 'Noise or filed away by a team member. Kept but hidden from working lists.',
}

/** Plural container name for a letter type: "Letters of complaint", "Chasers". */
export const letterContainerLabel = (kind: string) => {
    const single = sendKindLabel(kind)
    if (kind === 'initial_loc') return 'Letters of complaint'
    if (kind === 'new_loa') return 'New letters of authority'
    return /s$/.test(single) ? single : `${single}s`
}

export const bucketLabel = (bucket: Bucket): string => {
    const kind = letterKindOf(bucket)
    if (kind) return letterContainerLabel(kind)
    return FIXED_BUCKET_LABEL[bucket as 'waiting' | 'read' | 'archived']
}

export const bucketHint = (bucket: Bucket): string => {
    const kind = letterKindOf(bucket)
    if (kind) return `Conversations where an email answers a ${sendKindLabel(kind).toLowerCase()} we sent. Categories show what the reply said.`
    return FIXED_BUCKET_HINT[bucket as 'waiting' | 'read' | 'archived']
}

/** Letter types in display order (letters of complaint first). */
export const LETTER_KIND_ORDER = Object.keys(SEND_KIND_LABEL).filter((k) => k !== 'none')

export const STATE_LABEL: Record<ConversationState, string> = {
    awaiting_us: 'Awaiting us',
    awaiting_lender: 'Awaiting lender',
    awaiting_client: 'Awaiting client',
    closed: 'Closed',
    unknown: '—',
}

export const COUNTERPARTY_LABEL: Record<CounterpartyKind, string> = { lender: 'Lender', client: 'Client', other: 'Other' }
